import { Check, Copy, KeyRound, ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { motion } from 'motion/react';

interface Props {
  code: string;
  onContinue: () => Promise<void> | void;
  onClose: () => void;
}

export function AnonymousRecoveryModal({ code, onContinue, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const continueToPayment = async () => {
    try {
      setIsContinuing(true);
      await onContinue();
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <motion.div
      initial={{ scale: 0.92, y: 20 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.92, y: 20 }}
      onClick={(event) => event.stopPropagation()}
      className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl"
    >
      <div className="border-b border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-5 sm:px-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-purple-100 p-2.5 text-purple-600">
              <KeyRound className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Сохраните код восстановления</h2>
              <p className="mt-1 text-sm leading-5 text-gray-600">
                Перед оплатой сохраните этот код. Он поможет вернуть вашу анонимную
                сессию, если она пропадёт — например, после очистки данных браузера
                или в режиме инкогнито.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 transition-colors hover:bg-white hover:text-gray-900"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="px-6 py-6 sm:px-8">
        <label className="mb-2 block text-sm font-semibold text-gray-700">
          Ваш код
        </label>
        <div className="rounded-2xl border border-purple-200 bg-purple-50 p-4">
          <code className="block break-words font-mono text-sm leading-6 text-gray-900">
            {code}
          </code>
        </div>

        <button
          type="button"
          onClick={copyCode}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-purple-200 bg-white px-4 py-3 font-semibold text-purple-700 transition-colors hover:bg-purple-50"
        >
          {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          {copied ? 'Код скопирован' : 'Скопировать код'}
        </button>

        <div className="mt-5 flex gap-3 rounded-2xl bg-amber-50 p-4 text-sm leading-5 text-amber-800">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            Код выпускается только один раз. Сохраните его в надёжном месте —
            повторно запросить тот же код нельзя.
          </p>
        </div>

        <button
          type="button"
          disabled={isContinuing}
          onClick={continueToPayment}
          className="mt-6 w-full rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 text-base font-bold text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isContinuing ? 'Переходим к оплате…' : 'Перейти к оплате'}
        </button>
      </div>
    </motion.div>
  );
}
